import React, { useState, useEffect } from 'react';
import { getAdminProductTypes, addProductType, updateProductType, deleteProductType } from '../../api/api';

const AdminProductTypeManager = ({ onClose }) => {
  const [productTypes, setProductTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Add form state
  const [newName, setNewName] = useState('');
  const [newCode, setNewCode] = useState('');
  const [newApiParam, setNewApiParam] = useState('');
  const [newDescription, setNewDescription] = useState('');

  // Edit state
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editCode, setEditCode] = useState('');
  const [editApiParam, setEditApiParam] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editIsActive, setEditIsActive] = useState(true);

  useEffect(() => {
    fetchProductTypes();
  }, []);

  const fetchProductTypes = async () => {
    try {
      setLoading(true);
      const data = await getAdminProductTypes();
      setProductTypes(data.productTypes || []);
      setError('');
    } catch (err) {
      console.error('Error fetching product types:', err);
      setError('Failed to load product types');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    
    if (!newName.trim() || !newCode.trim()) {
      setError('Name and Code are required');
      return;
    }

    try {
      setLoading(true);
      await addProductType(newName.trim(), newCode.trim(), newApiParam.trim(), newDescription.trim());
      
      // Reset form
      setNewName('');
      setNewCode('');
      setNewApiParam('');
      setNewDescription('');
      setError('');
      
      await fetchProductTypes();
    } catch (err) {
      console.error('Error adding product type:', err);
      setError(err.response?.data?.error || 'Failed to add product type');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (productType) => {
    setEditingId(productType.id);
    setEditName(productType.name);
    setEditCode(productType.code);
    setEditApiParam(productType.api_param || '');
    setEditDescription(productType.description || '');
    setEditIsActive(productType.is_active);
  };

  const handleSaveEdit = async (id) => {
    try {
      setLoading(true);
      await updateProductType(id, editName, editCode, editApiParam, editDescription, editIsActive);
      setEditingId(null);
      setError('');
      await fetchProductTypes();
    } catch (err) {
      console.error('Error updating product type:', err);
      setError(err.response?.data?.error || 'Failed to update product type');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product type?')) {
      return;
    }

    try {
      setLoading(true);
      await deleteProductType(id);
      setError('');
      await fetchProductTypes();
    } catch (err) {
      console.error('Error deleting product type:', err);
      setError(err.response?.data?.error || 'Failed to delete product type');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center rounded-t-2xl">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">🏷️ Manage Product Types</h2>
            <p className="text-sm text-gray-600 mt-1">Add new product types with URL parameters</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
          >
            ×
          </button>
        </div>

        <div className="p-6 space-y-6">
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {/* Add New Product Type Form */}
          <form onSubmit={handleAdd} className="bg-pink-50 rounded-xl p-4 border-2 border-pink-200">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">➕ Add New Product Type</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Canvas Print"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Code <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newCode}
                  onChange={(e) => setNewCode(e.target.value.toUpperCase())}
                  placeholder="CANVAS"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent uppercase"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  API Parameter
                </label>
                <input
                  type="text"
                  value={newApiParam}
                  onChange={(e) => setNewApiParam(e.target.value)}
                  placeholder="product=CANVAS"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">Leave empty for default (no parameter)</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <input
                  type="text"
                  value={newDescription}
                  onChange={(e) => setNewDescription(e.target.value)}
                  placeholder="Canvas prints on premium material"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="mt-4 px-6 py-2 bg-pink-600 hover:bg-pink-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50"
            >
              ➕ Add Product Type
            </button>
          </form>

          {/* Product Types List */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800">📋 Existing Product Types</h3>

            {loading && productTypes.length === 0 ? (
              <div className="text-center py-8 text-gray-500">Loading...</div>
            ) : productTypes.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No product types found</div>
            ) : (
              <div className="space-y-3">
                {productTypes.map((productType) => (
                  <div
                    key={productType.id}
                    className={`border rounded-xl p-4 ${
                      productType.is_active ? 'bg-white border-gray-200' : 'bg-gray-100 border-gray-300'
                    }`}
                  >
                    {editingId === productType.id ? (
                      // Edit Mode
                      <div className="space-y-3">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                            <input
                              type="text"
                              value={editName}
                              onChange={(e) => setEditName(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
                            <input
                              type="text"
                              value={editCode}
                              onChange={(e) => setEditCode(e.target.value.toUpperCase())}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm uppercase"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">API Parameter</label>
                            <input
                              type="text"
                              value={editApiParam}
                              onChange={(e) => setEditApiParam(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                            <input
                              type="text"
                              value={editDescription}
                              onChange={(e) => setEditDescription(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm"
                            />
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id={`active-${productType.id}`}
                            checked={editIsActive}
                            onChange={(e) => setEditIsActive(e.target.checked)}
                            className="rounded"
                          />
                          <label htmlFor={`active-${productType.id}`} className="text-sm text-gray-700">
                            Active
                          </label>
                        </div>

                        <div className="flex gap-2">
                          <button
                            onClick={() => handleSaveEdit(productType.id)}
                            disabled={loading}
                            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
                          >
                            ✅ Save
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            disabled={loading}
                            className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
                          >
                            ❌ Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      // View Mode
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className="text-lg font-semibold text-gray-800">{productType.name}</h4>
                            {!productType.is_active && (
                              <span className="px-2 py-1 bg-gray-500 text-white text-xs rounded">Inactive</span>
                            )}
                          </div>
                          
                          <div className="space-y-1 text-sm text-gray-600">
                            <p><strong>Code:</strong> {productType.code}</p>
                            <p>
                              <strong>API Parameter:</strong>{' '}
                              {productType.api_param ? (
                                <code className="bg-gray-100 px-2 py-1 rounded">?{productType.api_param}</code>
                              ) : (
                                <span className="text-gray-400">Default (no parameter)</span>
                              )}
                            </p>
                            {productType.description && (
                              <p><strong>Description:</strong> {productType.description}</p>
                            )}
                            <p className="text-xs text-gray-400">
                              Created: {new Date(productType.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>

                        <div className="flex gap-2 ml-4">
                          <button
                            onClick={() => handleEdit(productType)}
                            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition-colors"
                          >
                            ✏️ Edit
                          </button>
                          <button
                            onClick={() => handleDelete(productType.id)}
                            disabled={loading}
                            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="sticky bottom-0 bg-gray-50 px-6 py-4 rounded-b-2xl border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full py-3 bg-gray-700 hover:bg-gray-800 text-white rounded-xl font-semibold transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminProductTypeManager;

